import { Button, CircularProgress, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  verifyEmployeePIN,
  clockIn,
  clockOut,
  getEmployeeClockStatus,
} from "utils/employeeService";
import { format } from "date-fns";
import { formatDisplayDate, formatDisplayDateTime } from "utils/dateFormat";
import { FaClock, FaSignInAlt, FaSignOutAlt, FaUser } from "react-icons/fa";
import { formatDuration, minutesBetween } from "utils/timesheetUtils";

const EmployeeClockIn = () => {
  const [pin, setPin] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isClocking, setIsClocking] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [clockStatus, setClockStatus] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handlePINSubmit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) {
      setError("Please enter your PIN");
      return;
    }

    setIsVerifying(true);
    setError("");

    try {
      const employeeData = await verifyEmployeePIN(pin);
      setEmployee(employeeData);
      const status = await getEmployeeClockStatus(employeeData.id);
      setClockStatus(status);
    } catch (error) {
      setError(
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        "Invalid PIN. Please try again."
      );
      setEmployee(null);
      setClockStatus(null);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClockIn = async () => {
    if (!employee) return;

    setIsClocking(true);
    try {
      await clockIn(employee.id);
      toast.success("Clocked in successfully!");
      const status = await getEmployeeClockStatus(employee.id);
      setClockStatus(status);
    } catch (error) {
      toast.error(
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        "Error clocking in"
      );
    } finally {
      setIsClocking(false);
    }
  };

  const handleClockOut = async () => {
    if (!employee) return;

    setIsClocking(true);
    try {
      await clockOut(employee.id);
      toast.success("Clocked out successfully!");
      const status = await getEmployeeClockStatus(employee.id);
      setClockStatus(status);
    } catch (error) {
      toast.error(
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        "Error clocking out"
      );
    } finally {
      setIsClocking(false);
    }
  };

  const handleLogout = () => {
    setEmployee(null);
    setClockStatus(null);
    setPin("");
    setError("");
  };

  const RightPanel = !employee ? (
    <div className="bg-app-surface rounded-3xl shadow-2xl p-8 border border-app-border-subtle relative overflow-hidden">
      <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-blue-50" />
      <div className="pointer-events-none absolute -left-16 bottom-0 h-40 w-40 rounded-full bg-indigo-50" />
      <div className="relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-2xl mb-4 border border-blue-200">
            <FaClock className="text-primary text-xl" />
          </div>
          <h2 className="text-2xl font-semibold text-app-text mb-1">Enter your PIN</h2>
          <p className="text-app-text-muted text-sm">
            We’ll verify your PIN and show your current clock-in status.
          </p>
        </div>

        <form onSubmit={handlePINSubmit} className="space-y-6">
          <div>
            <TextField
              fullWidth
              label="PIN"
              type="number"
              value={pin}
              onChange={(e) => {
                const next = e.target.value.replace(/[^0-9]/g, "");
                setPin(next);
                setError("");
              }}
              error={!!error}
              helperText={error}
              autoFocus
              inputProps={{
                maxLength: 20,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              disabled={isVerifying}
            />
            <p className="mt-1 text-[11px] text-app-text-muted">
              Only numbers are allowed. Do not share your PIN with anyone.
            </p>
          </div>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isVerifying || !pin.trim()}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              borderRadius: "999px",
              textTransform: "none",
            }}
          >
            {isVerifying ? (
              <>
                <CircularProgress size={20} className="mr-2" />
                Verifying PIN...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </form>
      </div>
    </div>
  ) : (
    <div className="bg-app-surface rounded-3xl shadow-2xl p-8 border border-app-border-subtle">
      {/* Employee Info */}
      <div className="text-center mb-6 pb-6 border-b border-app-border">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
          <FaUser className="text-primary text-3xl" />
        </div>
        <h2 className="text-2xl font-bold text-app-text mb-1">{employee.name}</h2>
        <p className="text-app-text-secondary text-sm">{employee.jobTitle || "Employee"}</p>
        {employee.department && (
          <p className="text-app-text-secondary text-xs mt-1">{employee.department}</p>
        )}
      </div>

      {/* Current Status */}
      {clockStatus && (
        <div className="mb-6">
          <div
            className={`p-4 rounded-xl border-2 ${clockStatus.isClockedIn
              ? "bg-green-50 border-green-200"
              : "bg-app-surface-alt border-app-border"
              }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-app-text-secondary mb-1">Current Status</p>
                <p
                  className={`text-lg font-bold ${clockStatus.isClockedIn ? "text-green-700" : "text-app-text"
                    }`}
                >
                  {clockStatus.isClockedIn ? "Clocked In" : "Clocked Out"}
                </p>
              </div>
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${clockStatus.isClockedIn ? "bg-green-500" : "bg-gray-400"
                  }`}
              >
                <FaClock className="text-white text-xl" />
              </div>
            </div>
            {clockStatus.isClockedIn && clockStatus.lastClockIn && (
              <div className="mt-3 pt-3 border-t border-green-200">
                <p className="text-xs text-app-text-secondary">
                  Clocked in at:{" "}
                  <span className="font-semibold text-app-text">
                    {formatDisplayDateTime(clockStatus.lastClockIn)}
                  </span>
                </p>
                <p className="text-xs text-app-text-secondary mt-1">
                  Time on shift:{" "}
                  <span className="font-semibold text-app-text">
                    {formatDuration(minutesBetween(clockStatus.lastClockIn, now))}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Clock In/Out Buttons */}
      <div className="space-y-3">
        {!clockStatus?.isClockedIn ? (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleClockIn}
            disabled={isClocking}
            startIcon={<FaSignInAlt />}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              borderRadius: "12px",
              backgroundColor: "#10b981",
              "&:hover": {
                backgroundColor: "#059669",
              },
            }}
          >
            {isClocking ? (
              <>
                <CircularProgress size={20} className="mr-2" />
                Clocking In...
              </>
            ) : (
              "Clock In"
            )}
          </Button>
        ) : (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleClockOut}
            disabled={isClocking}
            startIcon={<FaSignOutAlt />}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              borderRadius: "12px",
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              },
            }}
          >
            {isClocking ? (
              <>
                <CircularProgress size={20} className="mr-2" />
                Clocking Out...
              </>
            ) : (
              "Clock Out"
            )}
          </Button>
        )}

        <Button
          fullWidth
          variant="outlined"
          size="medium"
          onClick={handleLogout}
          sx={{
            py: 1.5,
            fontSize: "0.95rem",
            borderRadius: "12px",
            borderColor: "#e5e7eb",
            color: "#6b7280",
            "&:hover": {
              borderColor: "#d1d5db",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          Use Different PIN
        </Button>
      </div>

      {/* Current Time */}
      <div className="mt-6 pt-6 border-t border-app-border text-center">
        <p className="text-xs text-app-text-secondary mb-1">Current Time</p>
        <p className="text-lg font-semibold text-app-text">
          {formatDisplayDateTime(now)}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl">
        <div className="grid gap-8 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] items-stretch">
          {/* Left: Brand / Info panel */}
          <div className="hidden md:flex flex-col justify-between rounded-3xl border border-slate-800 bg-gradient-to-br from-blue-600/20 via-slate-900 to-indigo-700/40 p-8 shadow-2xl shadow-blue-900/40">
            <div>
              <div className="inline-flex items-center gap-3 rounded-full bg-slate-900/60 px-4 py-1 border border-slate-700/80 mb-6">
                <FaClock className="text-blue-400" />
                <span className="text-xs font-medium tracking-wide text-slate-200">
                  Smart Time Tracking
                </span>
              </div>
              <h1 className="text-3xl lg:text-4xl font-semibold text-white mb-3 leading-tight">
                Fast, secure
                <br />
                employee clock in/out.
              </h1>
              <p className="text-sm text-slate-300 max-w-sm">
                Use your unique PIN to start or end your shift. Your working hours are
                recorded instantly and kept in sync with the timesheet dashboard.
              </p>
            </div>
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-app-text-muted">
                  Today
                </p>
                <p className="mt-1 text-sm font-medium text-slate-100">
                  {formatDisplayDate(now)}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-app-text-muted">
                  Local Time
                </p>
                <p className="mt-1 text-sm font-medium text-slate-100">
                  {format(now, "hh:mm a")}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-app-text-muted">
                  Secure Access
                </p>
                <p className="mt-1 text-sm font-medium text-emerald-400">
                  PIN protected
                </p>
              </div>
            </div>
          </div>

          {/* Right: Auth / Clock-in panel */}
          {RightPanel}
        </div>
      </div>
    </div>
  );
};

export default EmployeeClockIn;
